import styled, { css, keyframes } from "styled-components";

import { MOBILE } from "./mobile";

/* The person's domains: one grouped block, a row each */
/* One card per domain (or Keepo), apart, like the Keepos on /sites */
export const DomainList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  > li > button,
  > li > .card {
    display: flex;
    gap: 1.25rem;
    align-items: center;
    width: 100%;
    padding: 1rem 1.25rem 1rem 1rem;
    text-align: left;
    cursor: pointer;
    background: var(--color-neutral-dark);
    border: 1px solid transparent;
    border-radius: 1rem;
    transition: border-color 0.15s;

    &:hover {
      border-color: var(--color-neutral-base);
    }

    @media ${MOBILE} {
      gap: 1rem;
      padding: 1rem;
    }
  }

  .keepo-url {
    color: var(--color-neutral-white);
  }

  /* The Keepo's icon (or the globe), as a Keepo Link's: in place of a Keepo's thumbnail, same size */
  .thumb {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    overflow: hidden;
    color: var(--color-neutral-150);
    background: var(--color-neutral-darkness);
    border-radius: 12px;

    /* the Keepo's favicon or logo (often a photo): round, as on a Keepo's card */
    img {
      width: 64%;
      height: 64%;
      object-fit: cover;
      border-radius: 100%;
    }

    @media ${MOBILE} {
      width: 40px;
      height: 40px;
    }
  }

  .empty {
    padding: 1.25rem;
    font-size: 0.8125rem;
    color: var(--color-neutral-150);
  }

  .icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    color: var(--color-neutral-white);
    background: var(--color-brand-blue);
    border-radius: 0.625rem;
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  .domain {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--color-neutral-white);

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .meta {
    overflow: hidden;
    font-size: 0.75rem;
    color: var(--color-neutral-150);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .chevron {
    flex-shrink: 0;
    color: var(--color-neutral-150);
  }
`;

/* The domain's screen: a notice right after buying it (registered, or waiting for the Pix/boleto) */
/* Right after buying a domain (My domains): a card with its state (Keepo's loading while it's registered, a check
   once it's theirs), the domain on its own line (cut with "…"), and Open when it's ready */
export const Banner = styled.div<{ $done: boolean }>`
  display: flex;
  gap: 0.875rem;
  align-items: center;
  padding: 1rem 1.25rem 1rem 1rem;
  font-family: Inter, sans-serif;
  background: var(--color-neutral-dark);
  border: 1px solid ${({ $done }) => ($done ? "rgba(87, 242, 139, 0.35)" : "var(--color-neutral-dark)")};
  border-radius: 1rem;
  transition: border-color 0.3s;

  .state {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    font-size: 1.125rem;
    font-weight: 800;
    color: ${({ $done }) => ($done ? "#0d3d1c" : "var(--color-neutral-white)")};
    background: ${({ $done }) => ($done ? "var(--color-brand-plus)" : "var(--color-neutral-base)")};
    border-radius: 100%;
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.1875rem;
    min-width: 0;
  }

  .label {
    font-size: 0.6875rem;
    font-weight: 800;
    color: ${({ $done }) => ($done ? "var(--color-brand-plus)" : "var(--color-neutral-150)")};
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .domain {
    overflow: hidden;
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .detail {
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }
`;

/* The domain list's cards, with Skeletons in place of the content */
export const SkeletonList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  li {
    display: flex;
    gap: 1.25rem;
    align-items: center;
    padding: 1rem 1.25rem 1rem 1rem;
    background: var(--color-neutral-dark);
    border: 1px solid transparent;
    border-radius: 1rem;

    @media ${MOBILE} {
      gap: 1rem;
      padding: 1rem;
    }
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 0;
  }
`;

/* A domain's status under it, like a Keepo's "● Ativo" on /sites; being registered, the dot pulses */
const statusDotPulse = keyframes`
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
`;

export const DomainStatus = styled.span<{ $status: "active" | "expiring" | "expired" | "pending" }>`
  display: inline-flex;
  gap: 0.375rem;
  align-items: center;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1rem;
  color: ${({ $status }) =>
    ({
      active: "var(--color-feedback-success)",
      expiring: "#ffb066",
      expired: "#ff8aa0",
      pending: "var(--color-neutral-150)",
    })[$status]};

  &::before {
    width: 6px;
    height: 6px;
    content: "";
    background-color: currentColor;
    border-radius: 100%;
    animation: ${({ $status }) =>
      $status === "pending"
        ? css`
            ${statusDotPulse} 1.2s ease-in-out infinite
          `
        : "none"};
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
  }
`;

export * from "./connect";
